/**
 * The commissioner runs it — `FFL Pass 05 Draft Order.dc.html` 1f, `draft-order-brief.md` §5.
 *
 * ONE IRREVERSIBLE ACTION, so everything that makes it auditable sits above it: the finishing order
 * the draw will read, the seed it will record, and who has not submitted a ranking yet. 2014-II-1 §5
 * wants the draw made in a live setting with the other GMs watching, so this is built to be put on
 * a television with nine people in the room — which is also why the finishing order is shown rather
 * than assumed. It is what prevents the "wrong standings" argument afterwards.
 *
 * THE LOTTERY ITSELF IS UNCHANGED (§0.1). `selectionOrder`, `runLottery`, `applyToiletBowlPriority`
 * and `LOTTERY_ODDS` keep their signatures and their answers; this file draws the screen around
 * them and does no arithmetic of its own.
 *
 * REHEARSAL WRITES NOTHING. No `persist`, no `lotteryDrawnAt`, no broadcast — a local order from a
 * local seed, shown on the same reveal component with the banner on. It is the only way to see what
 * the room will see without spending the one draw the league gets.
 *
 * WHAT 1f DOES NOT DRAW, and is here anyway: resolving preferences into slots and publishing them.
 * The canvas is the pre-draw state and stops at the button; §7 of 2020-II-1 still needs the order
 * back to the league by 8:00 PM, and there is nowhere else to do it from.
 */

import { useEffect, useMemo, useState } from 'react';
import { AnchorMark, Citations, Linkify } from './RuleText.js';
import { dock } from './dock.js';
import { managerShort } from '../domain/identity.js';
import { idToken } from '../store/session.js';
import {
  drawFor, rankingsForResolve, resolveDraftSlots, seededRng,
} from '../domain/draftOrder.js';
import { seedLabel, weightOf } from '../domain/lotteryReveal.js';
import { LotteryReveal } from './LotteryReveal.js';
import last from '../data/season-2025.json';
import { LOTTERY_BASIS, REGULAR_SEASON_SOURCE } from '../domain/season2025.js';
import { blocking, launchConsole, readiness } from '../domain/lotteryLaunch.js';
import { Console } from './Console.js';
import { swapPositions } from '../domain/thirdPlaceSwap.js';
import {
  inCureWindow, publicationState, publishAt, resolutionLine,
  type ResolutionSnapshot,
} from '../domain/publication.js';
import { bothZones } from '../domain/deadlines.js';
import { writeFailed } from './writeFailed.js';
import { rankingCensus } from '../domain/rankingCensus.js';
import { choiceNote } from '../domain/orderLedger.js';
import type {
  Docket, DraftPreference, DraftState, LeagueSettings, LeagueStore,
} from '../store/types.js';

const SEASON = 2026;
const TEAMS = 10;

const LAST = last as {
  standings: Array<{ rank: number; team: string; gm: string }>;
  thirdPlace: string; fourthPlace: string;
  toiletBowlWinner: string; toiletBowlConfirmed: boolean; toiletBowlBasis: string;
};
/*
 * THE DRAW IS WEIGHTED BY THE BASIS, NOT BY THE ORDER OF FINISH (Pass 06 §1). `runLottery`
 * weights by index, so whichever array reaches it IS the odds table — and the order of finish
 * hands the four non-playoff GMs each other's weights. See `season2025.ts`.
 */

/**
 * A fresh seed, recorded with the result so any GM can replay the draw and get the same order
 * (2014-II-1 §5). Generated when the screen opens rather than when the button is pressed, because
 * the design shows it BEFORE the draw — the room gets to see the number that produced the answer,
 * which is the online stand-in for watching the balls come out.
 */
function freshSeed(): number {
  const buf = new Uint32Array(1);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(buf);
    return buf[0]! % 2_147_483_647;
  }
  return Math.floor(performance.now() * 1000) % 2_147_483_647;
}

export function LotteryRunner({ gm, store }: { gm: string; store: LeagueStore }) {
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [prefs, setPrefs] = useState<DraftPreference[]>([]);
  const [swap, setSwap] = useState(false);
  const [seed, setSeed] = useState<number>(() => freshSeed());
  const [pastOpen, setPastOpen] = useState(false);
  const [pastAt, setPastAt] = useState('');
  const [pastErr, setPastErr] = useState<string | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);
  /** A local run: its own order, its own start, and nothing written anywhere. */
  const [rehearsal, setRehearsal] =
    useState<{ order: string[]; top4: string[]; at: number } | null>(null);
  /** When a rehearsal was last walked, for the card that says so. Local, like the rehearsal. */
  const [rehearsedAt, setRehearsedAt] = useState<number | null>(null);
  /*
   * THE ATTENDANCE REGISTER AND THE CALENDAR, both read rather than assumed.
   *
   * 2014-II-1 §5 asks for a live setting, so the launcher states who is actually there — and the
   * only place that knows is the docket the committee is sitting on. The deadline is read for the
   * same reason: "rankings close on 31 August" is the sentence that makes the ranking line a
   * footnote rather than an alarm, and a hard-coded date in it would be wrong the first year
   * anything moves.
   */
  const [docket, setDocket] = useState<Docket | null>(null);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([
      store.draftState(SEASON), store.preferences(SEASON),
      store.docket(SEASON), store.settings(SEASON),
    ])
      .then(([ds, ps, dk, st]) => {
        if (!live) return;
        setDraft(ds);
        setPrefs(ps);
        setDocket(dk);
        setSettings(st);
        setSwap(ds?.thirdFourthSwap ?? false);
        if (ds?.lotterySeed != null) setSeed(ds.lotterySeed);
      });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'preferences' || what === 'draftState' || what === 'docket'
        || what === 'settings') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  /* Memoised for the reason `DraftPrefs` used to document: a fresh `[]` every render is a
     dependency that changes on every render, and this one feeds two of them. */
  const order = useMemo(() => draft?.selectionOrder ?? [], [draft]);
  const finishOrder = useMemo(
    () => (draft?.finishOrder?.length === TEAMS ? draft.finishOrder : LOTTERY_BASIS),
    [draft],
  );
  const toilet = draft?.toiletBowlWinner ?? LAST.toiletBowlWinner;
  const third = draft?.thirdPlace ?? LAST.thirdPlace;
  const fourth = draft?.fourthPlace ?? LAST.fourthPlace;

  const drawn = order.length === TEAMS;
  /*
   * ONE CENSUS, shared with Home and the draft-order strip. `missing` is the men the resolve will
   * have NO list for — not the men who have not pressed the button, which is a different and much
   * longer list. See `domain/rankingCensus.ts`.
   */
  const census = useMemo(() => rankingCensus(prefs, TEAMS), [prefs]);
  const saved = useMemo(() => new Set(census.savedBy), [census]);
  const missing = useMemo(() => {
    const have = new Set([...census.sentBy, ...census.savedBy]);
    return finishOrder.filter((g) => !have.has(g.toUpperCase()));
  }, [census, finishOrder]);

  /**
   * WHAT THE RULE PRODUCES FROM THE INPUTS AS THEY STAND, every render.
   *
   * Not what this card shows — see `resolved` below, which reads the stored order — but the thing
   * to hold it against. `functions/src/resolve.ts` runs the identical calculation over the
   * identical inputs, so a difference between this and `draft.order` means the stored order was
   * worked out from something other than what is in front of us now.
   */
  const fresh = useMemo((): string[] | null => {
    if (!drawn) return null;
    /* One statement of which lists the resolve reads, shared with the function. */
    const byGm = rankingsForResolve(prefs);
    /*
     * BEFORE THE RESOLVE, NOT AFTER IT (Pass 06 §3). The option trades lottery RESULTS: 3rd takes
     * 4th's turn and then picks from his own list at that turn. Swapping the resolved slots
     * afterwards handed him the slot 4th had chosen off 4th's list, which is a different — and
     * usually worse — answer for the man exercising the right.
     */
    const walking = swap && third && fourth ? swapPositions(order, third, fourth) : order;
    return [...resolveDraftSlots(walking, byGm, TEAMS).entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([g]) => g);
  }, [drawn, order, prefs, swap, third, fourth]);

  const resolved = useMemo((): [string, number][] | null => {
    if (!drawn) return null;
    /*
     * ONCE THE RESOLVE HAS RUN, THE STORED ORDER IS THE ANSWER — this card must not show one of
     * its own.
     *
     * It used to, and on 31 August it printed a different board from the one every other screen
     * was showing, under a footer reading "published · every manager sees this order", which no
     * manager did. A screen showing a decided fact reads the fact; the recomputation above exists
     * to CHECK it, not to replace it.
     */
    const settled = draft?.order?.length === TEAMS;
    if (settled) return draft!.order.map((g, i) => [g, i + 1]);
    return fresh ? fresh.map((g, i) => [g, i + 1]) : null;
  }, [drawn, draft, fresh]);

  /*
   * WHAT THE SEED IN THE FIELD WOULD PRODUCE, recomputed as it is typed. Same call the live run
   * makes, so the board on screen before anything is written is the board that would be written.
   */
  /**
   * IS THE STORED ORDER STILL WHAT THE RULE PRODUCES?
   *
   * ASKED BY COMPARISON, not by timestamp, and the difference is the whole of this fix. The first
   * version asked "has anybody's ranking moved since `resolvedAt`", which catches the case it was
   * written for — Matt sending his list at 7:17 PM after the 5 o'clock resolve — and misses the
   * one that happened next: the resolve itself was corrected. A rule change leaves every timestamp
   * exactly where it was and every stored order wrong, and the screen sat there with no offer to
   * re-run because, by its own test, nothing had happened. Tim: *"no reset available -- luke's
   * still at #9."*
   *
   * Comparing the answer to the inputs catches both, and needs no list of the ways an order can
   * go out of date.
   */
  const stale = useMemo(
    () => Boolean(fresh && draft?.order?.length === TEAMS
      && draft.order.some((g, i) => g.toUpperCase() !== fresh[i]?.toUpperCase())),
    [fresh, draft],
  );

  /**
   * WHOSE RANKING MOVED SINCE THE RESOLVE READ IT — the usual reason, named.
   *
   * The three hours between §5 and §7 are a cure window, so a ranking arriving inside it is the
   * window working. What was missing was anything that SAID so. `updatedAt`, not `submittedAt`: a
   * man editing a list he already sent changes the outcome exactly as much as one sending his
   * first. Empty when the order went stale for some other reason, which is why it captions the
   * comparison above rather than standing in for it.
   */
  const movedSince = useMemo(() => {
    const at = draft?.resolvedAt ? Date.parse(draft.resolvedAt) : NaN;
    if (Number.isNaN(at)) return [];
    return prefs
      .filter((p) => p.updatedAt && Date.parse(p.updatedAt) > at)
      .map((p) => p.gm.toUpperCase())
      .sort();
  }, [prefs, draft]);

  /*
   * WHAT THE SEED IN THE FIELD WOULD PRODUCE, recomputed as it is typed. Same call the live run
   * makes, so the board on screen before anything is written is the board that would be written.
   */
  const preview = useMemo(
    () => drawFor(finishOrder, seededRng(seed), toilet || null),
    [finishOrder, seed, toilet],
  );

  async function persist(patch: Partial<DraftState>) {
    const next: DraftState = {
      season: SEASON,
      order: draft?.order ?? [],
      status: draft?.status ?? 'setup',
      onTheClock: draft?.onTheClock ?? 1,
      ...draft,
      ...patch,
      updatedAt: new Date().toISOString(),
    };
    await store.saveDraftState(next);
    setDraft(next);
  }

  /**
   * What a rejected write says, in words a commissioner can act on.
   *
   * Firestore's own message is `Missing or insufficient permissions`, which names neither the
   * document nor the reason, and a manager reading it has no idea whether he lacks the role or
   * the rules lack the case. Naming both possibilities is the difference between "it is broken"
   * and "one of these two things is true".
   */
  /** The one irreversible action. Writing `lotteryDrawnAt` is what switches every screen to 1d. */
  async function run() {
    if (drawn) return;
    const picked = drawFor(finishOrder, seededRng(seed), toilet || null);
    await persist({
      finishOrder: [...finishOrder],
      toiletBowlWinner: toilet || null,
      thirdPlace: third || null,
      fourthPlace: fourth || null,
      lotterySeed: seed,
      selectionOrder: picked.order,
      /* Stored with the result — the order alone cannot say who was drawn. See `drawFor`. */
      lotteryTop4: picked.top4,
      lotteryDrawnAt: new Date().toISOString(),
      published: false,
    });
  }

  /**
   * RECORD A DRAW THAT ALREADY HAPPENED.
   *
   * On 26 August the live run failed — a hooks-order fault took the reveal down the moment it
   * started — so the room watched the REHEARSAL and that is the board nine people saw and agreed
   * on. `run()` cannot express it: it draws a fresh seed, which would produce a different order
   * and quietly overwrite the one the league lived through.
   *
   * 2014-II-1 §5 wants the draw made "in a live setting where other GMs will be present to view
   * the process". A board the room watched together satisfies that; what it was missing is the
   * replayable seed, and `seedhunt.mts` recovered one that reproduces it exactly. So this writes
   * the same four fields `run()` writes, from a seed a commissioner supplies rather than one the
   * machine invents, and stamps it with the instant the room actually saw it.
   *
   * IT IS STILL THE SAME ARITHMETIC. The order is not typed in — it is DRAWN, from the seed,
   * through `drawFor`, exactly as the live path draws it. A commissioner cannot enter a board
   * here that the machine would not produce, which is the whole reason this is a seed field and
   * not ten dropdowns.
   */
  async function record(atIso: string) {
    if (drawn) return;
    const picked = drawFor(finishOrder, seededRng(seed), toilet || null);
    await persist({
      finishOrder: [...finishOrder],
      toiletBowlWinner: toilet || null,
      thirdPlace: third || null,
      fourthPlace: fourth || null,
      lotterySeed: seed,
      selectionOrder: picked.order,
      lotteryTop4: picked.top4,
      lotteryDrawnAt: atIso,
      published: false,
    });
  }

  /** No write, no broadcast, and a different seed so nobody mistakes it for the real answer. */
  function rehearse() {
    setRehearsedAt(Date.now());
    const { order, top4 } = drawFor(finishOrder, seededRng(freshSeed()), toilet || null);
    /*
     * A rehearsal keeps its provenance too — it is the screen the room will watch, so the boundary
     * and the "placed by rule" tag have to be as true here as on the night. A rehearsal that draws
     * a different line is not a rehearsal.
     */
    setRehearsal({ order, top4, at: Date.now() });
  }

  /*
   * PUBLICATION GOES THROUGH THE FUNCTION NOW (Pass 06 §7), not through a client write.
   *
   * The schedule publishes at 8:00 PM whether or not anybody is here, and the two overrides — a
   * commissioner who is satisfied early, and a real dispute — go to the same endpoint so that
   * "who published this, and when" has exactly one answer. The security rules refuse a client
   * write to `publishedAt`, `hold` and the audit snapshot for the same reason.
   *
   * `order` is still written from here, because that is the DRAW, not the publication.
   */
  /*
   * THE PRE-FLIGHT, COMPUTED BEFORE ANY EARLY RETURN — and the position in the file is the whole
   * point of this comment.
   *
   * It sat below `if (rehearsal) return …`, which meant pressing Rehearse rendered the component
   * with one fewer `useMemo` than the render before it. React counts hooks by call order, so that
   * is not a subtle bug: it throws "Rendered fewer hooks than expected" and takes the screen down.
   * The rehearsal — the one feature whose entire job is to be safe to press — was the thing that
   * broke it. Reported by Tim on the evening of the draw, 26 August.
   *
   * Nothing below may move above this line, and nothing here may move below one.
   */
  const absent = docket?.absent ?? [];
  const ranksCloseOn = settings?.preferenceDeadline
    ? bothZones(settings.preferenceDeadline)
    : '31 August';

  const launch = useMemo(() => ({
    present: TEAMS - absent.length,
    teams: TEAMS,
    finishConfirmed: REGULAR_SEASON_SOURCE.confirmed,
    finishSource: REGULAR_SEASON_SOURCE.source,
    toiletBowl: toilet || null,
    toiletBowlConfirmed: LAST.toiletBowlConfirmed,
    shortName: managerShort,
    ranked: TEAMS - missing.length,
    ranksCloseOn,
    drawn,
  }), [absent.length, toilet, missing.length, ranksCloseOn, drawn]);

  const ready = useMemo(() => readiness(launch), [launch]);
  const bar = useMemo(() => launchConsole(launch, ready, false), [launch, ready]);

  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * WHOSE RANKING IS OPEN, AND ONLY EVER ONE. See the `Rankings in` card below — a single slot
   * rather than a set is the mechanism, not a shortcut: it makes reading one GM's list an act
   * aimed at that GM, and reading all ten ten deliberate acts nobody performs while scrolling.
   */
  const [checking, setChecking] = useState<string | null>(null);

  async function act(
    action: 'publish' | 'hold' | 'release' | 'resolve' | 'correct', reason?: string,
  ) {
    setBusy(action);
    setProblem(null);
    try {
      const token = await idToken();
      const r = await fetch('/api/publication', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, reason, season: SEASON }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) setProblem(out.error ?? out.why ?? `The server refused (${r.status}).`);
      /*
       * NOTHING IS WRITTEN LOCALLY. The function is the only writer, so the screen waits for the
       * subscription to bring the change back — which also means what a commissioner sees is what
       * every other screen sees, rather than an optimistic guess that might not have landed.
       */
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Could not reach the server.');
    } finally {
      setBusy(null);
    }
  }

  if (rehearsal) {
    /*
     * DOCKED. This overlay is `position: fixed; inset: 0` and is supposed to take the whole
     * screen — §5 has the reveal shown on a television with nine people watching. On a phone it
     * did not: it began below the tab strip and its way-out button sat on top of rows 3 and 4,
     * because `main` carries pull-to-refresh's transform and a transform captures every fixed
     * descendant. `inset: 0` was resolving against the content column, not the viewport.
     */
    return dock(
      <div className="dolrehearsal">
        <LotteryReveal
          selectionOrder={rehearsal.order}
          top4={rehearsal.top4}
          finishOrder={finishOrder}
          me={gm}
          startedAt={rehearsal.at}
          teams={TEAMS}
          toiletBowlWinner={toilet || null}
          thirdPlace={third || null}
          fourthPlace={fourth || null}
          rehearsal
          onDismiss={() => setRehearsal(null)}
        />
        <button type="button" className="dolsecondary" onClick={() => setRehearsal(null)}>
          End the rehearsal
        </button>
      </div>,
    );
  }

  return (
    <Linkify>
      {/*
        AND THE LIVE RUN SAYS SO TOO. Same swallow, same consequence — a commissioner pressing
        `Run the lottery` in front of nine people and getting a page that still reads "not run
        yet" has no way to tell a refused write from a slow one.
      */}
      {runErr && <p className="note bad">{runErr}</p>}
      {!drawn && (
        <Console
          actor={gm}
          line={bar.line}
          standing={blocking(ready) || 'The lottery has not been run'}
          primary={bar.primary}
          question={bar.question}
          consequence={bar.consequence}
          blocked={bar.blocked}
          reversible={[{ id: 'rehearse', label: 'Rehearse it' }]}
          onPrimary={() => { setRunErr(null); void run().catch((e: unknown) => setRunErr(writeFailed(e))); }}
          onAction={rehearse}
        />
      )}
      <section className="dolrun">
        <header className="dolrunhd">
          <h2>Run the {SEASON} lottery</h2>
          <span className="dolcommish">Commissioner</span>
        </header>

        <div className="dolrungrid">
          <div className="dolruncol">
            {/*
              What the draw will READ. Showing it is the whole point of §5: nine people watching a
              result they cannot check is how the "wrong standings" argument starts.
            */}
            <section className="dolcard dolfinish">
              <div className="dolcardhd">
                <span className="dolcaps">Finishing order it will read</span>
                <span className="dolmut">{SEASON - 1} final standings</span>
              </div>
              <ol className="dolfinishlist">
                {finishOrder.map((g, i) => (
                  <li key={g} className={g.toUpperCase() === gm.toUpperCase() ? 'mine' : ''}>
                    <span className="dolfinno">{i + 1}</span>
                    <span className="dolfinname">{managerShort(g)}</span>
                    <span className="dolfinwt">{weightOf(finishOrder, g) ?? 0}%</span>
                  </li>
                ))}
              </ol>
              {/*
                THE AUDIT BLOCK (17j): everything the draw will read, above the one irreversible
                act. The claims and the seed used to sit in cards of their own; nine people
                checking a result need it in one place.
              */}
              <p className="dolaudit">
                Toilet Bowl · <b>{toilet ? managerShort(toilet) : 'nobody'}</b>
                {toilet ? ' — guaranteed the second call' : ''}
                {third && <> · 3rd <b>{managerShort(third)}</b></>}
                {fourth && <> · 4th <b>{managerShort(fourth)}</b></>}
                {third && fourth ? ' (the 2018-II-5 swap pair)' : ''}
              </p>
              <p className="dolaudit">
                Seed to be recorded: <code>{seedLabel(seed, draft?.lotteryDrawnAt ?? Date.now())}</code>
                {missing.length > 0
                  ? <> · missing rankings: <b className="warn">{missing.map(managerShort).join(', ')}</b></>
                  : <> · every ranking is in</>}
              </p>
            </section>

            {/*
              BEFORE YOU RUN IT — four lines, three states, every one of them derived.
              The block this replaces had a single red panel, and the thing it most often shouted
              about was rankings, which close five days AFTER the draw. See `domain/lotteryLaunch.ts`
              for the whole of that argument; the short version is that a commissioner who reads a
              red panel two minutes before a live event either delays it for nothing or stops
              reading red panels.
            */}
            <section className="dolcard dolready">
              <span className="dolcaps">Before you run it</span>
              <ul className="dolreadylist">
                {ready.map((l) => (
                  <li key={l.id} className={l.state}>
                    <span className="dolreadystate">{l.state}</span>
                    <span className="dolreadytext">
                      {l.text}
                      {l.note && (
                        <span className="dolreadynote" title={l.note}>{l.note}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {/*
              RANKINGS IN — who has turned one in, and, on purpose, what he sent (Tim, 31 August).

              THE HISTORY, BECAUSE THE ANSWER MOVED TWICE. The lists were printed in full to any
              commissioner; that was cut on 27 August, because the handoff's model is "sealed with
              counts shown" and the three people who can open this pane are the three with
              something to gain from reading them. Tim reversed it on the 31st for a case the
              sealed model had not accounted for: a GM who is not sure his ranking saved has
              nobody to ask. A seal that costs the league its ability to answer "did you get mine"
              is protecting the wrong thing.

              SO IT OPENS ONE MAN AT A TIME. The summary line — the tick and the stamp — is what
              chases the stragglers and answers the question, and it is all that is on screen by
              default. The list itself is behind a tap, on one GM, and closes when the next one
              opens. Reading all ten is still possible and is now ten deliberate acts, which is
              the difference between a lookup and a browse.

              IT INVENTS NOTHING AND WRITES NOTHING. Every figure here is the `submittedAt` stamp
              and the `ranked` array already on the preference, shown back unaltered — the same
              two facts the GM's own receipt card shows him, so the two can be read against each
              other when somebody says his ranking is wrong.
            */}
            <section className="dolcard dolranksin">
              <div className="dolcardhd">
                <span className="dolcaps">Rankings in</span>
                <span className="dolmut">{census.count}</span>
              </div>
              {/*
                THE HEADLINE COUNTS SUBMISSIONS, because the resolve does — see
                `draftOrder.rankingsForResolve`. A list saved and never sent is not read, so its
                author takes §6's earliest free slot exactly as if he had ranked nothing.

                It said the opposite here for a week, in as many words, and this line is why the
                31 August board went out wrong in the first place: a commissioner told that both
                counted had no reason to chase the two men whose lists were about to be ignored.
                Tim, 31 August: "if there are draft rankings, those should be known to the
                commissioners as well" — knowing is what the list below is for, and it is not the
                same as counting.
              */}
              {census.saved > 0 && (
                <p className="dolmut">
                  {census.sent} sent · {census.saved} saved but never sent, which the resolve will
                  not read — each of them is one tap from the slot he actually wants.
                </p>
              )}
              <ul className="dolinlist">
                {finishOrder.map((g) => {
                  const p = prefs.find((x) => x.gm.toUpperCase() === g.toUpperCase());
                  const at = p?.submittedAt ?? null;
                  const isDraft = saved.has(g.toUpperCase());
                  const state = at ? 'in' : isDraft ? 'draft' : 'out';
                  const open = checking === g.toUpperCase();
                  return (
                    <li key={g} className={state}>
                      <span className={`dolsubmark ${state}`}>
                        <span aria-hidden="true">{at ? '\u2713' : ''}</span>
                        <span className="dolvh">
                          {at ? 'submitted' : isDraft ? 'saved but never submitted' : 'nothing'}
                        </span>
                      </span>
                      <span className="dolinname">{managerShort(g)}</span>
                      <span className="dolinat">
                        {at ? `submitted ${bothZones(at)}`
                          : isDraft
                            ? 'saved but never sent — not read; he takes the earliest slot left (\u00a76)'
                            : 'nothing yet — takes the earliest slot left (\u00a76)'}
                      </span>
                      {(p?.ranked.length ?? 0) > 0 && (
                        <button type="button" className="linkbtn" aria-expanded={open}
                          onClick={() => setChecking(open ? null : g.toUpperCase())}>
                          {open ? 'Hide' : 'Check it'}
                        </button>
                      )}
                      {open && p && (
                        <ol className="prefreveal"
                          aria-label={`${managerShort(g)}'s ${at ? 'submitted' : 'saved'} ranking`}>
                          {p.ranked.map((slot, i) => (
                            <li key={slot}><span className="prno">{i + 1}</span>Slot {slot}</li>
                          ))}
                        </ol>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="dolmut">
                Open one at a time, to answer a GM who asks whether his ranking landed. What he
                ranked stays off every other screen until the order publishes. A saved list is
                worth a message rather than a chase — he has one, he just has not sent it.
              </p>
            </section>
          </div>

          <div className="dolruncol">
            <section className="dolcard dolseed">
              <span className="dolcaps">Seed</span>
              <output className="dolseedfield">{seedLabel(seed, draft?.lotteryDrawnAt ?? Date.now())}</output>
              <p className="dolmut">
                Recorded with the result so any GM can replay the draw and get the same order
                (2014-II-1 §5).
              </p>
            </section>

            <section className="dolcard">
              <span className="dolcaps">When you run it</span>
              <p className="dolruncopy">
                Every GM&rsquo;s screen switches to the reveal at the same moment. The order is
                final and cannot be re-run.
              </p>
            </section>

            {/*
              THE RUN MOVED TO THE CONSOLE (handoff §2). It was a full-width black slab with the
              safe action as a ghost outline underneath — the loudest control on the screen being
              the one that cannot be undone, which is the arrangement every other console in this
              app was built to avoid. It arms now, like the rest of them.
            */}
            {/*
              THE DRAW THAT HAPPENED SOMEWHERE ELSE. Folded shut, because on any ordinary night
              this is the wrong door and the one beside it is right — a card standing open offering
              to record a past draw, on a screen whose job is to run a live one, is an invitation
              to record the wrong thing.
            */}
            <section className="dolcard dolpast">
              <button type="button" className="dolpasthd" aria-expanded={pastOpen}
                onClick={() => setPastOpen((v) => !v)}>
                <span className="dolcaps">It has already been drawn</span>
                <i className="kdcaret" aria-hidden="true" />
              </button>
              {pastOpen && (
                <>
                  <p className="dolruncopy">
                    For a draw the room watched that never reached the record — a rehearsal the
                    league agreed on, or a run that failed halfway. The board is not typed in: it
                    is drawn from the seed, the same way {' '}
                    <b>Run the lottery</b> draws it, so nothing can be recorded here that the
                    machine would not have produced.
                  </p>
                  <label className="notefield">
                    Seed
                    <input type="number" value={seed} min={1} disabled={drawn}
                      onChange={(e) => setSeed(Number(e.target.value) || 1)} />
                  </label>
                  {/*
                    IN YOUR OWN CLOCK, AND SAID BACK IN THE LEAGUE'S.

                    `datetime-local` has no time zone: it means whatever the wall clock said where
                    the typist is. Half this league is on Mountain time and the rulebook, the
                    reveal's status bar and every deadline on this screen are written in Eastern —
                    so a commissioner in Denver typing the 8:04 he remembers from the ET
                    announcement would stamp the record two hours late, and nothing on screen
                    would contradict him. The echo underneath is the confirmation, and it costs
                    one line.
                  */}
                  <label className="notefield">
                    When the room saw it — in your own clock
                    <input type="datetime-local" value={pastAt} disabled={drawn}
                      onChange={(e) => setPastAt(e.target.value)} />
                  </label>
                  {pastAt && Number.isFinite(Date.parse(pastAt)) && (
                    <p className="dolmut">
                      That is <b>{bothZones(new Date(Date.parse(pastAt)).toISOString())}</b>.
                    </p>
                  )}
                  {/*
                    THE BOARD IT PRODUCES, BEFORE ANYTHING IS WRITTEN. A seed is six characters of
                    hex; a commissioner cannot tell from looking at it whether it is the right one.
                    What he can tell at a glance is whether these are the ten names the room saw.
                  */}
                  <ol className="dolpastboard">
                    {preview.order.map((g, i) => (
                      <li key={g} className={preview.top4.includes(g.toUpperCase()) ? 'drawn' : ''}>
                        <span className="dolfinno">{i + 1}</span>
                        <span className="dolfinname">{managerShort(g)}</span>
                        {toilet && g.toUpperCase() === toilet.toUpperCase()
                          && !preview.top4.includes(g.toUpperCase())
                          && <span className="dolfinwt">placed by rule</span>}
                      </li>
                    ))}
                  </ol>
                  <p className="dolmut">
                    Drawn four: {preview.top4.map((g) => managerShort(g)).join(', ')}.
                  </p>
                  {pastErr && <p className="note bad tight">{pastErr}</p>}
                  <div className="actions">
                    <button type="button" className="dolsubmit" disabled={drawn || busy !== null}
                      onClick={() => {
                        const t = Date.parse(pastAt);
                        if (!Number.isFinite(t)) { setPastErr('That is not a date.'); return; }
                        if (t > Date.now()) {
                          setPastErr('A draw that has already happened cannot be in the future.');
                          return;
                        }
                        setPastErr(null);
                        /*
                          A REFUSED WRITE MUST NOT LOOK LIKE A SUCCESS.
                          This is what cost a release cycle: the security rules denied the very
                          first `draftState` write — it was a CREATE, and the publication clause
                          could not express one — the rejection was swallowed by a bare `void`,
                          and the page carried on saying the lottery had not run. Tim pressed the
                          button, something happened, and nothing on screen disagreed with him.
                        */
                        void record(new Date(t).toISOString())
                          .catch((err: unknown) => setPastErr(writeFailed(err)));
                      }}>
                      Record this as the {SEASON} draw
                    </button>
                  </div>
                </>
              )}
            </section>

            <section className="dolcard dolrehearse">
              <span className="dolcaps">Rehearsal</span>
              <p className="dolruncopy">
                Runs the whole reveal against a throwaway seed. Nothing is written and nobody is
                notified — it is for checking the room can see the screen and for getting used to
                the controls.
              </p>
              {rehearsedAt && (
                <p className="dolmut">Last rehearsed {new Date(rehearsedAt).toLocaleTimeString()}.</p>
              )}
            </section>
          </div>
        </div>

        {/*
          After the draw. Not on the canvas — 1f is the pre-draw screen — but 2020-II-1 §7 still
          wants the resolved order back to the league by 8:00 PM and this is the only surface it
          can be done from.
        */}
        {resolved && (
          <section className="dolcard dolresolved">
            <div className="dolcardhd">
              <span className="dolcaps">Resolved first round</span>
              <AnchorMark anchor="third-place-swap" size="sm" />
            </div>
            {third && fourth && (
              <label className="dolswap">
                {/*
                  DEAD ONCE THERE IS AN ORDER, not only once it is published. This box previews
                  the option's effect by re-running the resolve, and the list above stops being a
                  re-run the moment one has been written — leaving it live would let a tick change
                  nothing while looking as though it had. The declaration itself is recorded on
                  Draft position; this was only ever the preview of it.
                */}
                <input type="checkbox" checked={swap}
                  disabled={draft?.order?.length === TEAMS}
                  onChange={(e) => setSwap(e.target.checked)} />
                <span>
                  {managerShort(third)} exercises the swap with {managerShort(fourth)} — optional,
                  and allowed even if {managerShort(fourth)} drew the better slot (2018-II-5).
                </span>
              </label>
            )}
            <ol className="dolfinishlist">
              {resolved.map(([g, slot]) => (
                <li key={g}>
                  <span className="dolfinno">{slot}</span>
                  <span className="dolfinname">{managerShort(g)}</span>
                  {/*
                    THE LEDGER'S WORDING, off the list the resolve actually read. "down the list"
                    was covering three different things — second choice, a slot he never ranked,
                    and having ranked nothing at all — and it said it of a man whose unsent draft
                    this card was reading and the resolve was not.
                  */}
                  <span className="dolfinwt">
                    {choiceNote(
                      prefs.find((p) => p.gm.toUpperCase() === g.toUpperCase())?.ranked,
                      slot,
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <Publication
              draft={draft}
              busy={busy}
              problem={problem}
              stale={stale}
              moved={movedSince}
              onAct={act}
            />
          </section>
        )}

        <Citations ids={['2020-II-1', '2014-II-1', '2018-II-5']} />
      </section>
    </Linkify>
  );
}


/* -- resolve at five, publish at eight (Pass 06 §7) -------------------------------------------- */

/**
 * What the commissioner does about the resolved order, which on most nights is nothing.
 *
 * THE CARD LEADS WITH THE FACT THAT IT IS AUTOMATIC. §7 makes auto-publish the default rather than
 * the fallback — "published by 8:00 PM" is the rule, and nobody being at a keyboard must not make
 * the league miss it — so the first thing this says is when it will happen on its own. The two
 * buttons are for the two cases the brief names, and neither is the normal path.
 *
 * A HOLD NEEDS A REASON, and the league reads it. A commissioner who stops the clock silently
 * leaves nine people staring at an unpublished order assuming the app is broken, which is worse
 * than knowing there is a dispute.
 */
function Publication(
  { draft, busy, problem, stale, moved, onAct }: {
    draft: DraftState | null;
    busy: string | null;
    problem: string | null;
    /** The stored order is not what the rule produces from the inputs as they stand. */
    stale: boolean;
    /** GMs whose ranking has changed since the resolve read it — the usual reason, when it is. */
    moved: readonly string[];
    onAct: (
      a: 'publish' | 'hold' | 'release' | 'resolve' | 'correct', reason?: string,
    ) => void | Promise<void>;
  },
) {
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);

  const input = {
    preferenceDeadline: draft?.resolvedAt ? draft.resolvedAt : null,
    resolvedAt: draft?.resolvedAt ?? null,
    /* An order published before §7 existed has `published` and no stamp; read both. */
    publishedAt: draft?.publishedAt ?? (draft?.published ? draft.updatedAt : null),
    hold: draft?.hold ?? null,
  };
  const state = publicationState(input);
  const due = publishAt({ preferenceDeadline: draft?.resolvedAt ?? null });
  const snapshot = draft?.resolution as ResolutionSnapshot | undefined;

  return (
    <div className="dolpub">
      <div className="dolpubtop">
        <span className={`statepill ${state === 'published' ? 'violet' : state === 'held' ? 'amber' : 'grey'}`}>
          {state}
        </span>
        <span className="dolmut">
          {state === 'published' ? 'Every manager sees this order.'
            : state === 'held' ? `Held by ${managerShort(draft?.hold?.by ?? '')} — the league can see why.`
              : state === 'provisional'
                ? `Commissioners only until it publishes${due ? ` at ${bothZones(due)}` : ''}.`
                : 'Not resolved yet — the schedule runs it when rankings lock.'}
        </span>
      </div>

      {snapshot && <p className="dolmut">{resolutionLine(snapshot, 10)}</p>}

      {/*
        THE ORDER ON SCREEN IS OLDER THAN ITS OWN INPUTS, said out loud.
        Named, because "rankings have changed" sends a commissioner hunting and a name does not.
      */}
      {stale && (
        <p className="dolpubstale">
          {moved.length === 1
            ? `${managerShort(moved[0]!)} has changed his ranking since this order was worked out. `
            : moved.length > 1
              ? `${moved.map(managerShort).join(', ')} have changed their rankings since this `
                + 'order was worked out. '
              /* No ranking moved, so the resolve itself did — a rule correction, most likely. */
              : 'This order is not what the rules produce from the rankings on file. '}
          {draft?.publishedAt
            ? 'The published board is out of date.'
            : 'Re-resolve before publishing.'}
        </p>
      )}
      {draft?.hold && <p className="dolpubhold">&ldquo;{draft.hold.reason}&rdquo;</p>}
      {problem && <p className="dolpubproblem">{problem}</p>}

      {asking ? (
        <div className="dolpubask">
          <label htmlFor="holdwhy" className="dolmut">Why is it being held?</label>
          <input id="holdwhy" type="text" value={reason} maxLength={140}
            placeholder="Ryan says his ranking did not save"
            onChange={(e) => setReason(e.target.value)} />
          <div className="dolpubrow">
            <button type="button" className="dolsecondary" disabled={!reason.trim() || busy !== null}
              onClick={() => { void onAct('hold', reason); setAsking(false); setReason(''); }}>
              Hold publication
            </button>
            <button type="button" className="dolsecondary" onClick={() => setAsking(false)}>
              Never mind
            </button>
          </div>
        </div>
      ) : (
        <div className="dolpubrow">
          {state !== 'published' && (
            <button type="button" className="dolsubmit" disabled={busy !== null || !draft?.resolvedAt}
              onClick={() => void onAct('publish')}>
              {busy === 'publish' ? 'Publishing…' : 'Publish now'}
            </button>
          )}
          {state === 'held' ? (
            <button type="button" className="dolsecondary" disabled={busy !== null}
              onClick={() => void onAct('release')}>
              Lift the hold
            </button>
          ) : state !== 'published' && (
            <button type="button" className="dolsecondary" disabled={busy !== null || !draft?.resolvedAt}
              onClick={() => setAsking(true)}>
              Hold&hellip;
            </button>
          )}
          {/*
            The correction path. Re-running is safe by construction — same inputs, same output —
            so this is a plain button rather than a confirmation: the worst it can do is compute
            the same answer twice.
          */}
          {state === 'provisional' && inCureWindow({ ...input, preferenceDeadline: draft?.resolvedAt ?? null }) && (
            <button type="button" className="dolsecondary" disabled={busy !== null}
              onClick={() => void onAct('resolve')}>
              {busy === 'resolve' ? 'Re-resolving…' : 'Re-resolve after a correction'}
            </button>
          )}
          {/*
            AND THE SAME THING AFTER PUBLICATION, which every path in this screen used to refuse.
            A published board is meant to be final, and it must not be final in the one case where
            it is WRONG. Offered only when the stored order actually disagrees with what the rules
            produce, so it is a fix for a stated problem rather than a button that re-rolls the
            league's draft order because it is there. The superseded order goes into the snapshot.
          */}
          {state === 'published' && stale && (
            <button type="button" className="dolsecondary" disabled={busy !== null}
              onClick={() => void onAct('correct')}>
              {busy === 'correct' ? 'Correcting…' : 'Re-resolve and republish'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
